// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import {VNode} from 'snabbdom';

/*eslint-disable-next-line import/no-cycle*/
import {ContextPanelMenuEl} from 'common/widgets/context-panel/context-panel-menu';

import {PositioningStrategy} from '../drop-menu/types';
import {DropMenuEl} from '../drop-menu';
import type {ItemsMenu} from './index';
import {SectionEl, SectionRowEl} from './section';
// eslint-disable-next-line import/no-cycle
import {SectionItemEl, SectionItemSubMenuEl} from './section/item';
import {ItemClickEvent, WrappedSection} from './types';

const EmptySearchResult = ({message}) => (
  <ul sel='.screen-options-list'>
    <li sel='.list-option.list-empty'>
      <div sel='.option-label'>{message}</div>
    </li>
  </ul>
);

const SectionList = ({comp, sections}: {comp: ItemsMenu; sections: WrappedSection[]}) => {
  const sectionsHint = comp.attr(`sections-hint`);
  const searchFilter = comp.attr(`search-filter`);
  const alignAllText =
    comp.attr(`align-all-text`) &&
    sections.some((section) => section.iconRenderer || section.items.some((item) => item.wrapped?.iconRenderer));
  const showMatches = !comp.attr(`disable-search-filter-matching-parts`);
  const {focusedItemIndex, isPillFocused, fullyShownSections} = comp.state;
  const {contextPanelTransform} = comp;
  const hasContextPanelContentSlot = comp.helpers.hasSlot(`context-panel-content`);
  let sectionsEl: VNode;
  if (sections.length) {
    sectionsEl = (
      <>
        {sectionsHint && <div sel='.sections-hint'>{sectionsHint}</div>}
        {sections.map((section, idx) => (
          <SectionEl
            section={section}
            sectionIdx={idx}
            hideSectionTitle={
              (section.isHidden && !fullyShownSections[section.sectionIndex]) || Boolean(section.expansionToggle)
            }
            areSectionItemsHidden={
              (section.isHidden || Boolean(section.expansionToggle)) && !fullyShownSections[section.sectionIndex]
            }
            numOriginalItems={comp.state.sections[section.sectionIndex]?.items.length || 0}
            i18n={comp.getContext(`i18n`)}
            sectionLeftIcon={section.sectionLeftIcon}
          >
            {section.items.map((item) => {
              const sectionItem = item.wrapped;
              const isFocused = focusedItemIndex === item.index;
              const isItemSubMenuOpen = comp.isSubItemsOpen(item.index);
              const selectedTheme =
                (section.selectedTheme && comp.getSelectedTheme(section.selectedTheme)) || comp.getSelectedTheme();
              return (
                <SectionRowEl
                  allowPointerEventsAlways={hasContextPanelContentSlot}
                  i18n={comp.getContext(`i18n`)}
                  item={item}
                  isFocused={isFocused}
                  selectedTheme={selectedTheme}
                  contextPanelTransform={contextPanelTransform}
                  onClick={comp.helpers.handleClickItem}
                  onMouseEnter={comp.helpers.handleMouseEnterItem}
                >
                  <SectionItemEl
                    item={item}
                    theme={comp.attr(`theme`)}
                    isPillFocused={isPillFocused}
                    selectedTheme={selectedTheme}
                    checkboxIcon={comp.attr(`checkbox-icon`)}
                    isFocused={isFocused}
                    alignAllText={alignAllText}
                    showMatches={showMatches}
                    actionsRenderer={sectionItem.actionsRenderer ?? section.actionsRenderer}
                    iconRenderer={sectionItem.iconRenderer ?? section.iconRenderer}
                    onActionClick={comp.helpers.handleClickPill}
                    onActionMouseEnter={comp.helpers.handleMouseEnterPill}
                    onActionMouseLeave={comp.helpers.handleMouseLeavePill}
                    fullyShownSections={comp.state.fullyShownSections}
                    i18n={comp.getContext(`i18n`)}
                    renderOverflowMenu={section.renderOverflowMenu}
                    onDeleteTag={section.onDeleteTag}
                  />
                  {isItemSubMenuOpen &&
                    (sectionItem.subMenuRenderer ? (
                      sectionItem.subMenuRenderer((selectedValue: string | number, additionalInfo: Obj) => {
                        const itemClickEvent: ItemClickEvent = comp.getClickItemEvent({
                          item: {
                            ...item,
                            wrapped: {
                              ...item.wrapped,
                              value: selectedValue,
                              additionalInfo,
                            },
                          },
                          modifierKeys: null,
                        });

                        comp.helpers.handleSubItemsClickItem(itemClickEvent);
                      }, comp)
                    ) : (
                      <SectionItemSubMenuEl
                        sections={sectionItem.subSections}
                        alignAllText={comp.attr(`align-all-text`)}
                        selectedTheme={selectedTheme}
                        contextPanelTransform={contextPanelTransform}
                        keydownProxyEl={comp.keydownProxyEl || comp}
                        onNavigateBack={comp.helpers.handleSubItemsNavigateBack}
                        onClickItem={comp.helpers.handleSubItemsClickItem}
                        onFocusItem={comp.helpers.propagateEvent}
                        onMouseEnter={comp.helpers.handleSubMenuMouseEnter}
                        theme={comp.attr(`sub-menu-theme`)}
                      />
                    ))}
                </SectionRowEl>
              );
            })}
          </SectionEl>
        ))}
      </>
    );
  }

  let emptyMessageEl: VNode;
  if (comp.getFilledSections(sections, {excludeAlwaysShowLabel: true}).length === 0) {
    if (searchFilter) {
      emptyMessageEl = <EmptySearchResult message={comp.attr(`empty-search-results-message`)} />;
    } else {
      emptyMessageEl = <EmptySearchResult message={comp.attr(`empty-message`)} />;
    }
  }

  return (
    <>
      {sectionsEl}
      {emptyMessageEl}
    </>
  );
};

export function Template(this: ItemsMenu) {
  const {tooltipData} = this.state;
  const hasContextPanelContentSlot = this.helpers.hasSlot(`context-panel-content`);
  const sections = this.helpers.getDisplaySections();
  return (
    <div
      sel='.items-menu'
      hook={{postpatch: this.helpers.handlePostPatch}}
      class={{
        [`${this.attr(`theme`)}-theme`]: true,
        'disable-first-item-padding': this.attr(`disable-first-item-padding`),
      }}
    >
      <div
        sel='.screen-list-container'
        class={{'has-sections': sections.length > 0}}
        on={{
          scroll: this.helpers.handleScroll,
          mouseleave: () => this.helpers.handleMouseLeave(),
          mouseenter: () => this.debouncedUnFocusItem.cancel(),
        }}
        hook={{
          insert: ({elm}) => this.handleInsertScreenListContainer(elm as HTMLElement),
        }}
      >
        <SectionList comp={this} sections={sections} />
      </div>
      {tooltipData && (
        <ContextPanelMenuEl
          attrs={{
            elref: `contextPanel`,
            placement: this.attr(`context-panel-alignment`),
            'project-is-free-plan': this.attr(`project-is-free-plan`),
          }}
          params={{
            data: tooltipData,
            positionParent: this.contextPanelPositionParentElement,
          }}
        />
      )}
      {hasContextPanelContentSlot && (
        <DropMenuEl
          attrs={{
            direction: this.attr(`context-panel-alignment`),
            offset: -8,
            skidding: 72,
            open: true,
            'position-strategy': PositioningStrategy.Absolute,
          }}
          props={{
            positionParent: this.contextPanelPositionParentElement,
          }}
        >
          <slot attrs={{name: `context-panel-content`}} />
        </DropMenuEl>
      )}
    </div>
  );
}
